import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  Incident,
  SystemStatus,
  AgentMode,
  RightPanelTab,
  DiagnosisStep,
} from '../types/autofix';
import { WebMCPTelemetryLog, WebMCPToolDefinition } from '../types/webmcp';
import { diagnosticEngine } from '../agent/DiagnosticEngine';
import { llmAgent } from '../agent/LLMAgent';
import { targetAppStore } from '../target-app/store';
import { REGISTERED_BUGS } from '../target-app/bugs';
import { modelContextInstance } from '../webmcp/polyfill';
import { webMCPRegistry } from '../webmcp/registry';
import { registerAllWebMCPTools } from '../webmcp/tools';
import { scanAndRegisterDeclarativeTools } from '../webmcp/declarative';

interface AutoFixContextValue {
  systemStatus: SystemStatus;
  activeIncident: Incident | null;
  incidentHistory: Incident[];
  agentMode: AgentMode;
  setAgentMode: (mode: AgentMode) => void;
  geminiApiKey: string;
  setGeminiApiKey: (key: string) => void;
  activeTab: RightPanelTab;
  setActiveTab: (tab: RightPanelTab) => void;
  telemetryLogs: WebMCPTelemetryLog[];
  registeredTools: WebMCPToolDefinition[];
  triggerBug: (bugId: string, customCrash?: any) => Promise<void>;
  resetAll: () => void;
  soundEnabled: boolean;
  setSoundEnabled: (enabled: boolean) => void;
}

const AutoFixContext = createContext<AutoFixContextValue | undefined>(undefined);

export const AutoFixProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [systemStatus, setSystemStatus] = useState<SystemStatus>('monitoring');
  const [activeIncident, setActiveIncident] = useState<Incident | null>(null);
  const [incidentHistory, setIncidentHistory] = useState<Incident[]>([]);
  const [agentMode, setAgentMode] = useState<AgentMode>('deterministic');
  const [geminiApiKey, setGeminiApiKey] = useState<string>('');
  const [activeTab, setActiveTab] = useState<RightPanelTab>('timeline');
  const [telemetryLogs, setTelemetryLogs] = useState<WebMCPTelemetryLog[]>([]);
  const [registeredTools, setRegisteredTools] = useState<WebMCPToolDefinition[]>([]);
  const [soundEnabled, setSoundEnabled] = useState(true);

  // Play subtle sound effects for dramatic hackathon demos
  const playSound = (type: 'incident' | 'healed' | 'step') => {
    if (!soundEnabled || typeof window === 'undefined') return;
    try {
      const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.connect(gain);
      gain.connect(audioCtx.destination);

      if (type === 'incident') {
        // Warning alert tone
        osc.frequency.setValueAtTime(440, audioCtx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(220, audioCtx.currentTime + 0.3);
        gain.gain.setValueAtTime(0.15, audioCtx.currentTime);
        gain.gain.linearRampToValueAtTime(0.01, audioCtx.currentTime + 0.3);
        osc.start();
        osc.stop(audioCtx.currentTime + 0.3);
      } else if (type === 'healed') {
        // High ascending success chime
        osc.frequency.setValueAtTime(523.25, audioCtx.currentTime); // C5
        osc.frequency.setValueAtTime(659.25, audioCtx.currentTime + 0.1); // E5
        osc.frequency.setValueAtTime(783.99, audioCtx.currentTime + 0.2); // G5
        osc.frequency.setValueAtTime(1046.5, audioCtx.currentTime + 0.3); // C6
        gain.gain.setValueAtTime(0.18, audioCtx.currentTime);
        gain.gain.linearRampToValueAtTime(0.01, audioCtx.currentTime + 0.45);
        osc.start();
        osc.stop(audioCtx.currentTime + 0.45);
      } else if (type === 'step') {
        // Soft click
        osc.frequency.setValueAtTime(800, audioCtx.currentTime);
        gain.gain.setValueAtTime(0.04, audioCtx.currentTime);
        gain.gain.linearRampToValueAtTime(0.001, audioCtx.currentTime + 0.05);
        osc.start();
        osc.stop(audioCtx.currentTime + 0.05);
      }
    } catch {
      // Audio context may require user interaction
    }
  };

  // Initialization: Register tools on mount
  useEffect(() => {
    registerAllWebMCPTools();
    setRegisteredTools(webMCPRegistry.getTools());

    // Listen for WebMCP telemetry logs
    const unsubTelemetry = modelContextInstance.onTelemetry((log) => {
      setTelemetryLogs((prev) => {
        const existingIdx = prev.findIndex((item) => item.id === log.id);
        if (existingIdx !== -1) {
          const updated = [...prev];
          updated[existingIdx] = log;
          return updated;
        }
        return [log, ...prev.slice(0, 49)];
      });
    });

    // Scan for declarative form tools
    const timer = setTimeout(() => {
      scanAndRegisterDeclarativeTools();
      setRegisteredTools(webMCPRegistry.getTools());
    }, 400);

    // Subscribe to diagnostic steps
    const unsubStep = diagnosticEngine.subscribeStep((step: DiagnosisStep) => {
      playSound('step');
    });

    // Subscribe to incident updates
    const unsubIncident = diagnosticEngine.subscribeIncident((inc: Incident) => {
      setActiveIncident(inc);
      if (inc.phase === 'detected') {
        setSystemStatus('incident');
        playSound('incident');
      } else if (inc.phase === 'resolved') {
        setSystemStatus('resolved');
        playSound('healed');
        setIncidentHistory((prev) => [inc, ...prev.filter((x) => x.id !== inc.id)]);
      } else {
        setSystemStatus('healing');
      }
    });

    // Listen for custom crash detection events dispatched from TargetApp components
    const handleCrashDetected = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail && detail.bugId) {
        triggerBug(detail.bugId, {
          errorMessage: detail.error?.message,
          stackTrace: detail.error?.stack || detail.componentStack,
          userAction: detail.action,
        });
      }
    };

    window.addEventListener('autofix-crash-detected', handleCrashDetected);

    return () => {
      unsubTelemetry();
      unsubStep();
      unsubIncident();
      clearTimeout(timer);
      window.removeEventListener('autofix-crash-detected', handleCrashDetected);
    };
  }, []);

  const triggerBug = async (bugId: string, customCrash?: any) => {
    const bug = REGISTERED_BUGS[bugId];
    if (!bug) return;

    if (bugId === 'calc-div-zero') {
      window.dispatchEvent(new CustomEvent('autofix-select-target', { detail: 'calculator' }));
    }

    // 1. Trigger the bug in the target app
    bug.trigger();
    setSystemStatus('incident');

    // 2. Switch to Timeline tab automatically so the judge sees the action immediately!
    setActiveTab('timeline');

    // 3. Trigger autonomous self-healing loop
    if (agentMode === 'llm' && geminiApiKey) {
      llmAgent.setConfig({ apiKey: geminiApiKey, provider: 'gemini' });
      await llmAgent.diagnoseWithLLM(bugId);
    } else {
      await diagnosticEngine.healIncident(bugId, customCrash);
    }
  };

  const resetAll = () => {
    targetAppStore.resetAll();
    setSystemStatus('monitoring');
    setActiveIncident(null);
  };

  return (
    <AutoFixContext.Provider
      value={{
        systemStatus,
        activeIncident,
        incidentHistory,
        agentMode,
        setAgentMode,
        geminiApiKey,
        setGeminiApiKey,
        activeTab,
        setActiveTab,
        telemetryLogs,
        registeredTools,
        triggerBug,
        resetAll,
        soundEnabled,
        setSoundEnabled,
      }}
    >
      {children}
    </AutoFixContext.Provider>
  );
};

export const useAutoFix = () => {
  const ctx = useContext(AutoFixContext);
  if (!ctx) {
    throw new Error('useAutoFix must be used within an AutoFixProvider');
  }
  return ctx;
};
